/**
 * TAP2BUY React Hooks for Amplify Gen 2
 *
 * These hooks use the Amplify GraphQL client to fetch data
 * from the Lambda functions that query DynamoDB.
 */

import { useMemo } from 'react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { generateClient } from 'aws-amplify/api';

// Lazy client initialization - only create when first used (after Amplify.configure())
let _client: ReturnType<typeof generateClient> | null = null;
function getClient() {
  if (!_client) {
    _client = generateClient();
  }
  return _client;
}

// Local type definitions (matching the GraphQL schema)
export interface Product {
  product_id: number;
  seller_id: number;
  sku: string;
  product_name: string;
  product_type: string;
  price: number;
  weight: number;
  quantity: number;
  description: string;
  short_description: string;
  image_url: string;
  images: string[];
  status: number;
  visibility: number;
  is_approved: number;
  is_deleted: number;
  categories: number[];
  category_names: string[];
  store_type: string;
  parent_product_id: number | null;
  child_product_ids: number[];
  url_key: string;
  is_gift_card: boolean;
  gift_card_config: string | null;
  configurable_options: string | null;
  configurable_options_products: string | null;
  created_at: string;
  updated_at: string;
  stripe_product_id: string | null;
  stripe_price_id: string | null;
}

export interface Category {
  category_id: number;
  name: string;
  parent_id: number;
  level: number;
  path: string;
  url_key: string;
  is_active: number;
  position: number;
  include_in_menu: number;
  description: string;
  image_url: string;
  created_at: string;
  updated_at: string;
}

export interface Seller {
  seller_id: number;
  email: string;
  firstname: string;
  lastname: string;
  shop_url: string;
  shop_title: string;
  logo_pic: string;
  supplied_by_image: string;
  company_description: string;
  website_url: string;
  is_active: number;
  stripe_account_id: string | null;
  stripe_onboarding_complete: boolean;
  created_at: string;
  updated_at: string;
}

export interface ShippingRule {
  rule_id: number;
  seller_id: number;
  name: string;
  price: number;
  sort_order: number;
  is_active: number;
  conditions: string;
  created_at: string;
  updated_at: string;
}

interface ProductsResult {
  items: Product[];
  lastEvaluatedKey: string | null;
  count: number;
  hasMore: boolean;
}

interface CategoriesResult {
  items: Category[];
  count: number;
}

// ============================================================
// GRAPHQL QUERIES
// ============================================================

const queries = {
  browseProducts: /* GraphQL */ `
    query BrowseProducts($storeType: String!, $limit: Int, $lastEvaluatedKey: String) {
      browseProducts(storeType: $storeType, limit: $limit, lastEvaluatedKey: $lastEvaluatedKey) {
        items {
          product_id seller_id sku product_name product_type price weight quantity
          description short_description image_url images status visibility
          is_approved is_deleted categories category_names store_type
          parent_product_id child_product_ids url_key is_gift_card
          created_at updated_at stripe_product_id stripe_price_id
        }
        lastEvaluatedKey count hasMore
      }
    }
  `,
  getProductsByCategory: /* GraphQL */ `
    query GetProductsByCategory($categoryId: Int!, $limit: Int, $lastEvaluatedKey: String) {
      getProductsByCategory(categoryId: $categoryId, limit: $limit, lastEvaluatedKey: $lastEvaluatedKey) {
        items {
          product_id seller_id sku product_name product_type price weight quantity
          description short_description image_url images status visibility
          is_approved is_deleted categories category_names store_type
          parent_product_id child_product_ids url_key is_gift_card
          created_at updated_at stripe_product_id stripe_price_id
        }
        lastEvaluatedKey count hasMore
      }
    }
  `,
  getProductsBySeller: /* GraphQL */ `
    query GetProductsBySeller($sellerId: Int!, $limit: Int, $lastEvaluatedKey: String) {
      getProductsBySeller(sellerId: $sellerId, limit: $limit, lastEvaluatedKey: $lastEvaluatedKey) {
        items {
          product_id seller_id sku product_name product_type price weight quantity
          description short_description image_url images status visibility
          is_approved is_deleted categories category_names store_type
          parent_product_id child_product_ids url_key is_gift_card
          created_at updated_at stripe_product_id stripe_price_id
        }
        lastEvaluatedKey count hasMore
      }
    }
  `,
  getProduct: /* GraphQL */ `
    query GetProduct($productId: Int!, $sellerId: Int!) {
      getProduct(productId: $productId, sellerId: $sellerId) {
        product_id seller_id sku product_name product_type price weight quantity
        description short_description image_url images status visibility
        is_approved is_deleted categories category_names store_type
        parent_product_id child_product_ids url_key is_gift_card gift_card_config
        configurable_options configurable_options_products
        created_at updated_at stripe_product_id stripe_price_id
      }
    }
  `,
  getProductById: /* GraphQL */ `
    query GetProductById($productId: Int!) {
      getProductById(productId: $productId) {
        product_id seller_id sku product_name product_type price weight quantity
        description short_description image_url images status visibility
        is_approved is_deleted categories category_names store_type
        parent_product_id child_product_ids url_key is_gift_card gift_card_config
        configurable_options configurable_options_products
        created_at updated_at stripe_product_id stripe_price_id
      }
    }
  `,
  getCategories: /* GraphQL */ `
    query GetCategories($activeOnly: Boolean, $menuOnly: Boolean) {
      getCategories(activeOnly: $activeOnly, menuOnly: $menuOnly) {
        items {
          category_id name parent_id level path url_key
          is_active position include_in_menu description image_url
          created_at updated_at
        }
        count
      }
    }
  `,
  getSeller: /* GraphQL */ `
    query GetSeller($sellerId: Int!) {
      getSeller(sellerId: $sellerId) {
        seller_id email firstname lastname shop_url shop_title
        logo_pic supplied_by_image company_description website_url
        is_active stripe_account_id stripe_onboarding_complete
        created_at updated_at
      }
    }
  `,
  getSellerByShopUrl: /* GraphQL */ `
    query GetSellerByShopUrl($shopUrl: String!) {
      getSellerByShopUrl(shopUrl: $shopUrl) {
        seller_id email firstname lastname shop_url shop_title
        logo_pic supplied_by_image company_description website_url
        is_active stripe_account_id stripe_onboarding_complete
        created_at updated_at
      }
    }
  `,
  getShippingRules: /* GraphQL */ `
    query GetShippingRules($sellerId: Int!) {
      getShippingRules(sellerId: $sellerId) {
        rule_id seller_id name price sort_order is_active conditions
        created_at updated_at
      }
    }
  `,
};

// ============================================================
// QUERY KEYS
// ============================================================

export const queryKeys = {
  products: {
    all: ['products'] as const,
    browse: (storeType: string) => ['products', 'browse', storeType] as const,
    byCategory: (categoryId: number) => ['products', 'category', categoryId] as const,
    bySeller: (sellerId: number) => ['products', 'seller', sellerId] as const,
    single: (productId: number, sellerId: number) =>
      ['products', 'single', productId, sellerId] as const,
    byId: (productId: number) => ['products', 'byId', productId] as const,
  },
  categories: {
    all: ['categories'] as const,
    single: (categoryId: number) => ['categories', categoryId] as const,
  },
  sellers: {
    single: (sellerId: number) => ['sellers', sellerId] as const,
    byShopUrl: (shopUrl: string) => ['sellers', 'shopUrl', shopUrl] as const,
  },
  shipping: {
    rules: (sellerId: number) => ['shipping', 'rules', sellerId] as const,
  },
};

// ============================================================
// PRODUCT HOOKS
// ============================================================

/**
 * Fetch a single product by ID (requires seller_id)
 */
export function useProduct(productId: number, sellerId: number) {
  return useQuery(
    queryKeys.products.single(productId, sellerId),
    async () => {
      const response: any = await getClient().graphql({
        authMode: 'apiKey',
        query: queries.getProduct,
        variables: { productId, sellerId },
      });
      return response.data.getProduct as Product;
    },
    {
      enabled: productId > 0 && sellerId > 0,
      staleTime: 5 * 60 * 1000,
    }
  );
}

/**
 * Fetch a product by ID only (backward compatibility for existing URLs)
 * Uses Scan - less efficient but works without seller_id
 */
export function useProductById(productId: number) {
  return useQuery(
    queryKeys.products.byId(productId),
    async () => {
      const response: any = await getClient().graphql({
        authMode: 'apiKey',
        query: queries.getProductById,
        variables: { productId },
      });
      return response.data.getProductById as Product;
    },
    {
      enabled: productId > 0,
      staleTime: 5 * 60 * 1000,
    }
  );
}

/**
 * Browse products with infinite scroll pagination
 */
export function useBrowseProducts(options: {
  storeType: string;
  pageSize?: number;
}) {
  const { storeType, pageSize = 20 } = options;

  return useInfiniteQuery(
    queryKeys.products.browse(storeType),
    async ({ pageParam }) => {
      const response: any = await getClient().graphql({
        authMode: 'apiKey',
        query: queries.browseProducts,
        variables: { storeType, limit: pageSize, lastEvaluatedKey: pageParam },
      });
      return response.data.browseProducts as ProductsResult;
    },
    {
      getNextPageParam: (lastPage) =>
        lastPage?.hasMore ? lastPage.lastEvaluatedKey : undefined,
      staleTime: 2 * 60 * 1000,
    }
  );
}

/**
 * Fetch products by seller with pagination
 */
export function useProductsBySeller(sellerId: number, pageSize = 50) {
  return useInfiniteQuery(
    queryKeys.products.bySeller(sellerId),
    async ({ pageParam }) => {
      const response: any = await getClient().graphql({
        authMode: 'apiKey',
        query: queries.getProductsBySeller,
        variables: { sellerId, limit: pageSize, lastEvaluatedKey: pageParam },
      });
      return response.data.getProductsBySeller as ProductsResult;
    },
    {
      getNextPageParam: (lastPage) =>
        lastPage?.hasMore ? lastPage.lastEvaluatedKey : undefined,
      enabled: sellerId > 0,
      staleTime: 2 * 60 * 1000,
    }
  );
}

/**
 * Fetch products by category with pagination
 */
export function useProductsByCategory(categoryId: number, pageSize = 20) {
  return useInfiniteQuery(
    queryKeys.products.byCategory(categoryId),
    async ({ pageParam }) => {
      const response: any = await getClient().graphql({
        authMode: 'apiKey',
        query: queries.getProductsByCategory,
        variables: { categoryId, limit: pageSize, lastEvaluatedKey: pageParam },
      });
      return response.data.getProductsByCategory as ProductsResult;
    },
    {
      getNextPageParam: (lastPage) =>
        lastPage?.hasMore ? lastPage.lastEvaluatedKey : undefined,
      enabled: categoryId > 0,
      staleTime: 2 * 60 * 1000,
    }
  );
}

// ============================================================
// CATEGORY HOOKS
// ============================================================

/**
 * Fetch all categories
 */
export function useCategories(options?: {
  activeOnly?: boolean;
  menuOnly?: boolean;
}) {
  const { activeOnly = true, menuOnly = false } = options ?? {};

  return useQuery(
    queryKeys.categories.all,
    async () => {
      const response: any = await getClient().graphql({
        authMode: 'apiKey',
        query: queries.getCategories,
        variables: { activeOnly, menuOnly },
      });
      return (response.data.getCategories?.items ?? []) as Category[];
    },
    {
      staleTime: 10 * 60 * 1000,
    }
  );
}

// ============================================================
// SELLER HOOKS
// ============================================================

/**
 * Fetch a seller by ID
 */
export function useSeller(sellerId: number) {
  return useQuery(
    queryKeys.sellers.single(sellerId),
    async () => {
      const response: any = await getClient().graphql({
        authMode: 'apiKey',
        query: queries.getSeller,
        variables: { sellerId },
      });
      return response.data.getSeller as Seller;
    },
    {
      enabled: sellerId > 0,
      staleTime: 10 * 60 * 1000,
    }
  );
}

/**
 * Fetch a seller by shop URL
 */
export function useSellerByShopUrl(shopUrl: string) {
  return useQuery(
    queryKeys.sellers.byShopUrl(shopUrl),
    async () => {
      const response: any = await getClient().graphql({
        authMode: 'apiKey',
        query: queries.getSellerByShopUrl,
        variables: { shopUrl },
      });
      return response.data.getSellerByShopUrl as Seller;
    },
    {
      enabled: !!shopUrl,
      staleTime: 10 * 60 * 1000,
    }
  );
}

// ============================================================
// SHIPPING HOOKS
// ============================================================

/**
 * Fetch shipping rules for a seller
 */
export function useShippingRules(sellerId: number) {
  return useQuery(
    queryKeys.shipping.rules(sellerId),
    async () => {
      const response: any = await getClient().graphql({
        authMode: 'apiKey',
        query: queries.getShippingRules,
        variables: { sellerId },
      });
      return (response.data.getShippingRules ?? []) as ShippingRule[];
    },
    {
      enabled: sellerId > 0,
      staleTime: 10 * 60 * 1000,
    }
  );
}

// ============================================================
// TREE BUILDING
// ============================================================

interface CategoryTreeNode {
  entity_id: number;
  value: string;
  parent_id: number;
  level: number;
  position: number;
  url_key: string;
  children: CategoryTreeNode[];
}

/**
 * Build category tree from flat list
 */
function buildCategoryTree(categories: Category[]): CategoryTreeNode[] {
  const categoryMap = new Map<number, CategoryTreeNode>();
  const tree: CategoryTreeNode[] = [];

  // First pass: create all nodes
  categories.forEach(cat => {
    categoryMap.set(cat.category_id, {
      entity_id: cat.category_id,
      value: cat.name,
      parent_id: cat.parent_id || 0,
      level: cat.level || 1,
      position: cat.position || 0,
      url_key: cat.url_key || '',
      children: [],
    });
  });

  // Second pass: build tree
  categoryMap.forEach(node => {
    if (node.parent_id === 0 || node.level === 1) {
      tree.push(node);
    } else {
      const parent = categoryMap.get(node.parent_id);
      if (parent) {
        parent.children.push(node);
      } else {
        tree.push(node);
      }
    }
  });

  // Sort by position
  const sortByPosition = (a: CategoryTreeNode, b: CategoryTreeNode) => a.position - b.position;
  tree.sort(sortByPosition);
  tree.forEach(node => {
    node.children.sort(sortByPosition);
    node.children.forEach(child => {
      child.children.sort(sortByPosition);
    });
  });

  return tree;
}

/**
 * Fetch categories as tree structure (for navigation)
 */
export function useCategoryTree(options?: {
  activeOnly?: boolean;
  menuOnly?: boolean;
}) {
  const { activeOnly = true, menuOnly = true } = options ?? {};

  return useQuery(
    [...queryKeys.categories.all, 'tree', activeOnly, menuOnly],
    async () => {
      const response: any = await getClient().graphql({
        authMode: 'apiKey',
        query: queries.getCategories,
        variables: { activeOnly, menuOnly },
      });
      const categories = (response.data.getCategories?.items ?? []) as Category[];
      return buildCategoryTree(categories);
    },
    {
      staleTime: 10 * 60 * 1000,
    }
  );
}

// ============================================================
// UTILITY HOOKS
// ============================================================

/**
 * Get current store type based on environment
 */
export function useStoreType(): string {
  return useMemo(() => {
    if (process.env.REACT_APP_GIFTGURU === 'true') return 'GIFTGURU';
    if (process.env.REACT_APP_TAP2BUY === 'true') return 'TAP2BUY';

    const hostname = window.location.hostname;
    if (hostname.includes('giftguru') || hostname.includes('gift.guru')) return 'GIFTGURU';
    if (hostname.includes('tap2gift')) return 'TAP2GIFT';

    return 'TAP2BUY';
  }, []);
}

/**
 * Combined hook for product detail page
 */
export function useProductDetail(productId: number, sellerId: number) {
  const productQuery = useProduct(productId, sellerId);
  const sellerQuery = useSeller(sellerId);

  return {
    product: productQuery.data,
    seller: sellerQuery.data,
    isLoading: productQuery.isLoading || sellerQuery.isLoading,
    error: productQuery.error || sellerQuery.error,
  };
}

/**
 * Combined hook for seller shop page
 */
export function useSellerShop(shopUrl: string) {
  const sellerQuery = useSellerByShopUrl(shopUrl);
  const sellerId = sellerQuery.data?.seller_id ?? 0;

  const productsQuery = useProductsBySeller(sellerId);
  const products = productsQuery.data?.pages.flatMap(page => page?.items || []) || [];

  return {
    seller: sellerQuery.data,
    products,
    isLoading: sellerQuery.isLoading,
    isLoadingProducts: productsQuery.isLoading,
    fetchNextPage: productsQuery.fetchNextPage,
    hasNextPage: productsQuery.hasNextPage ?? false,
    error: sellerQuery.error || productsQuery.error,
  };
}
